import {Drawer, Input, TextInput, Textarea} from '@mantine/core';
import {
  COMMON_DRAWER_STYLES,
  getDrawerSize,
} from '../../../../constants/drawerStyles';
import {Z_INDEX} from '../../../../constants/zIndex';
import {ClickToEditDateInputContainer} from '../../ClickToEditDateInputContainer';
import {ModalCancelButton} from '../ModalCancelButton';
import {ModalDeleteButton} from '../ModalDeleteButton';
import {ModalSubmitButton} from '../ModalSubmitButton';

export type FormDataEntryProgressStep = {
  stepOrder: number;
  stepName: string;
  deadline?: string;
  notes?: string; // NULL許容フィールド（バックエンドAPI仕様に合わせて修正）
  result?: string;
  startDate?: string;
  endDate?: string;
  place?: string;
  stepId?: number; // 編集時に使用
};

type Props = {
  formData: FormDataEntryProgressStep;
  opened: boolean;
  isSp?: boolean;
  editMode?: boolean;
  isLoading?: boolean;
  errors?: Partial<Record<keyof FormDataEntryProgressStep, string>>;
  onClose: () => void;
  onBack?: () => void;
  onFormChange: (
    field: keyof FormDataEntryProgressStep,
    value: string | number | undefined,
  ) => void;
  onFieldBlur?: (field: keyof FormDataEntryProgressStep) => void;
  onSubmit: () => void;
  onDelete?: () => void;
};

export const ProgressStepDrawer: React.FC<Props> = ({
  formData,
  opened,
  isSp = false,
  editMode = false,
  isLoading = false,
  errors = {},
  onClose,
  onBack,
  onFormChange,
  onFieldBlur,
  onSubmit,
  onDelete,
}) => {
  // 必須項目の入力チェック
  const isFormValid = formData.stepName.trim();

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={getDrawerSize(isSp)}
      title={editMode ? '選考予定編集' : '選考予定追加'}
      styles={COMMON_DRAWER_STYLES}
      zIndex={Z_INDEX.FORM_DRAWER}
    >
      <div className="flex flex-col gap-24">
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <TextInput
              id="stepName"
              type="text"
              label="選考予定"
              value={formData.stepName}
              onChange={(e) => onFormChange('stepName', e.target.value)}
              onBlur={() => onFieldBlur?.('stepName')}
              placeholder="例：一次面接"
              withAsterisk
              error={errors?.stepName}
              radius={24}
            />
          </div>
          <div className="flex flex-col gap-4">
            <Input.Label>締切</Input.Label>
            <ClickToEditDateInputContainer
              value={formData.deadline}
              onSave={(value) => onFormChange('deadline', value ?? '')}
              withTime
              error={errors?.deadline}
            />
          </div>
          {/* 日程: 開始日時と終了日時を横並びで表示 */}
          <div className="flex flex-col gap-4">
            <Input.Label>日程</Input.Label>
            <div className="flex items-center gap-8">
              <ClickToEditDateInputContainer
                value={formData.startDate}
                onSave={(value) => onFormChange('startDate', value ?? '')}
                withTime
                error={errors?.startDate}
                displayClassName="pc:flex-none pc:w-[200px]"
              />
              <span className="text-sm text-black-3">〜</span>
              <ClickToEditDateInputContainer
                value={formData.endDate}
                onSave={(value) => onFormChange('endDate', value ?? '')}
                withTime
                error={errors?.endDate}
                displayClassName="pc:flex-none pc:w-[200px]"
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <TextInput
              id="place"
              type="text"
              label="場所"
              value={formData.place || ''}
              onChange={(e) => onFormChange('place', e.target.value)}
              onBlur={() => onFieldBlur?.('place')}
              placeholder="例：オンライン、東京オフィス"
              error={errors?.place}
              radius={24}
            />
          </div>
          <div className="flex flex-col gap-4">
            <Textarea
              id="notes"
              label="メモ"
              value={formData.notes || ''}
              onChange={(e) => onFormChange('notes', e.target.value)}
              onBlur={() => onFieldBlur?.('notes')}
              placeholder="例：持ち物、服装、注意事項"
              error={errors?.notes}
            />
          </div>
        </div>
        <div className="flex items-center justify-center gap-16">
          {/* 追加時のみ戻るボタン */}
          {!editMode && onBack && (
            <ModalCancelButton onClick={onBack}>戻る</ModalCancelButton>
          )}

          {/* 編集時のみ削除ボタン */}
          {editMode && onDelete && (
            <ModalDeleteButton onClick={onDelete} isLoading={isLoading}>
              削除
            </ModalDeleteButton>
          )}

          {/* 保存ボタン */}
          <ModalSubmitButton
            onClick={onSubmit}
            disabled={!isFormValid}
            isLoading={isLoading}
          >
            {editMode ? '編集内容を保存' : '選考予定を追加'}
          </ModalSubmitButton>
        </div>
      </div>
    </Drawer>
  );
};
